import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useDropdownMenu, MenuPanel } from "@react/shared/ui/DropdownMenu"

const PANEL_MIN_WIDTH = 220
const SEARCH_FROM     = 10

const fold = (text) => `${text}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()

const SearchableSelect = ({ options, value, placeholder, clearable = true, onChange }) => {
  const { open, close, toggle, pos, btnRef, menuRef } = useDropdownMenu({ width: PANEL_MIN_WIDTH })
  const [query, setQuery] = useState("")
  const inputRef = useRef(null)

  const selected   = options.find((option) => String(option.id) === String(value))
  const searchable = options.length > SEARCH_FROM

  useEffect(() => {
    if (open && searchable) inputRef.current?.focus()
    if (!open) setQuery("")
  }, [open, searchable])

  const matches = useMemo(() => {
    const tokens = fold(query).split(/\s+/).filter(Boolean)
    if (tokens.length === 0) return options
    return options.filter((option) => {
      const haystack = fold(option.name)
      return tokens.every((token) => haystack.includes(token))
    })
  }, [options, query])

  const pick = (id) => {
    close()
    onChange(id)
  }

  const panelWidth = Math.max(Math.round(btnRef.current?.getBoundingClientRect().width || 0), PANEL_MIN_WIDTH)
  const item       = "w-full text-left px-2.5 py-1.5 rounded-md bg-transparent border-0 cursor-pointer text-[12.5px] text-slate-700 hover:bg-slate-100 truncate"

  return (
    <>
      <button
        ref       = {btnRef}
        type      = "button"
        onClick   = {toggle}
        className = "h-8 max-w-[190px] flex items-center justify-between gap-2 text-[12.5px] border border-slate-200 rounded-lg px-2 bg-white text-slate-700"
      >
        <span className="truncate">{selected ? selected.name : placeholder}</span>
        <FontAwesomeIcon icon="chevron-down" className="w-2 h-2 shrink-0 text-slate-400" />
      </button>

      {open && (
        <MenuPanel menuRef={menuRef} pos={pos} width={panelWidth} style={{ width: `${panelWidth}px` }} className="max-w-[80vw]">
          {searchable && (
            <input
              ref         = {inputRef}
              type        = "text"
              value       = {query}
              onChange    = {(e) => setQuery(e.target.value)}
              placeholder = {I18n.t("navigation.search_placeholder")}
              className   = "w-full h-8 mb-1 px-2 text-[12.5px] border border-slate-200 rounded-md text-slate-700"
            />
          )}
          <div className="max-h-[260px] overflow-auto">
            {clearable && (
              <button type="button" className={item} onClick={() => pick("")}>{placeholder}</button>
            )}
            {matches.map((option) => (
              <button
                key       = {option.id}
                type      = "button"
                className = {`${item}${String(option.id) === String(value) ? " bg-slate-100 font-semibold" : ""}`}
                onClick   = {() => pick(option.id)}
              >
                {option.name}
              </button>
            ))}
            {matches.length === 0 && (
              <div className="px-2.5 py-2 text-[12px] text-slate-400">{I18n.t("navigation.no_results")}</div>
            )}
          </div>
        </MenuPanel>
      )}
    </>
  )
}

export default SearchableSelect
